import type { OrderResponseDto } from "@/types/order.dto";

/** Typical thermal roll widths: 58mm (~219px @96dpi) or 80mm (~302px). */
export type ThermalPaperWidth = 58 | 80;

function escapeHtml(value: string | number | null | undefined) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function money(amount: number) {
  return `₹${Number(amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

/** Extract member code from POS notes like "POS billing · Member MBR748636". */
function extractMemberCode(notes?: string | null) {
  if (!notes) return null;
  const match = notes.match(/Member\s+([A-Za-z0-9_-]+)/i);
  return match?.[1] ?? null;
}

export function buildThermalReceiptHtml(
  order: OrderResponseDto,
  options?: { paperWidthMm?: ThermalPaperWidth },
): string {
  const paperWidthMm = options?.paperWidthMm ?? 80;
  const memberCode = extractMemberCode(order.customerNotes);
  const itemCount = order.items.reduce((sum, i) => sum + i.quantity, 0);

  const itemRows = order.items
    .map((item, index) => {
      const name = escapeHtml(item.productName);
      return `
        <div class="item">
          <div class="item-name">${index + 1}. ${name}</div>
          <div class="item-line">
            <span>${escapeHtml(item.quantity)} x ${escapeHtml(money(item.unitPrice))}</span>
            <span class="amt">${escapeHtml(money(item.totalPrice))}</span>
          </div>
        </div>`;
    })
    .join("");

  const discountRow =
    order.discountAmount > 0
      ? `<div class="row"><span>Discount${order.couponCode ? ` (${escapeHtml(order.couponCode)})` : ""}</span><span>-${escapeHtml(money(order.discountAmount))}</span></div>`
      : "";

  const deliveryRow =
    order.deliveryCharge > 0
      ? `<div class="row"><span>Delivery</span><span>${escapeHtml(money(order.deliveryCharge))}</span></div>`
      : "";

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Receipt ${escapeHtml(order.orderNumber)}</title>
  <style>
    @page {
      size: ${paperWidthMm}mm auto;
      margin: 0;
    }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #000;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    body {
      font-family: "Courier New", Courier, monospace;
      font-size: 12px;
      line-height: 1.35;
      width: ${paperWidthMm}mm;
      max-width: 100%;
      margin: 0 auto;
      padding: 3mm 2.5mm 6mm;
    }
    .center { text-align: center; }
    .store-name {
      font-size: 16px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      margin: 0 0 2px;
    }
    .sub { font-size: 11px; margin: 0; }
    .dash {
      border: none;
      border-top: 1px dashed #000;
      margin: 8px 0;
    }
    .solid {
      border: none;
      border-top: 1px solid #000;
      margin: 8px 0;
    }
    .meta { font-size: 11px; }
    .meta .row, .totals .row, .item-line {
      display: flex;
      justify-content: space-between;
      gap: 8px;
    }
    .item { margin-bottom: 6px; }
    .item-name {
      font-weight: 700;
      word-break: break-word;
    }
    .item-line { font-size: 11px; margin-top: 1px; }
    .amt { white-space: nowrap; font-weight: 700; }
    .totals .row { margin: 2px 0; }
    .grand {
      font-size: 14px;
      font-weight: 700;
      margin-top: 4px;
    }
    .thanks {
      margin-top: 10px;
      font-weight: 700;
      font-size: 12px;
    }
    .tiny { font-size: 10px; margin-top: 4px; }
    .barcode {
      margin-top: 8px;
      font-size: 14px;
      letter-spacing: 0.12em;
      font-weight: 700;
    }
    @media screen {
      body {
        box-shadow: 0 0 0 1px #ddd;
        min-height: 100vh;
      }
    }
  </style>
</head>
<body>
  <div class="center">
    <p class="store-name">${escapeHtml(order.storeName || "Store")}</p>
    <p class="sub">TAX INVOICE / CASH MEMO</p>
    <p class="sub">Powered by Bihari Mall</p>
  </div>

  <hr class="dash" />

  <div class="meta">
    <div class="row"><span>Bill No</span><span>${escapeHtml(order.orderNumber)}</span></div>
    <div class="row"><span>Date</span><span>${escapeHtml(formatDateTime(order.createdAt))}</span></div>
    <div class="row"><span>Cashier</span><span>POS Counter</span></div>
    <div class="row"><span>Payment</span><span>${escapeHtml(order.paymentMethodText)}</span></div>
    ${
      memberCode
        ? `<div class="row"><span>Member</span><span>${escapeHtml(memberCode)}</span></div>`
        : ""
    }
    <div class="row"><span>Customer</span><span>${escapeHtml(order.customerName)}</span></div>
    ${
      order.customerPhone
        ? `<div class="row"><span>Phone</span><span>${escapeHtml(order.customerPhone)}</span></div>`
        : ""
    }
  </div>

  <hr class="solid" />

  <div class="meta">
    <div class="row"><span><strong>ITEM</strong></span><span><strong>AMOUNT</strong></span></div>
  </div>
  <hr class="dash" />

  ${itemRows}

  <hr class="solid" />

  <div class="totals meta">
    <div class="row"><span>Items / Qty</span><span>${escapeHtml(order.items.length)} / ${escapeHtml(itemCount)}</span></div>
    <div class="row"><span>Subtotal</span><span>${escapeHtml(money(order.subTotal))}</span></div>
    ${discountRow}
    ${deliveryRow}
    <hr class="dash" />
    <div class="row grand"><span>TOTAL</span><span>${escapeHtml(money(order.totalAmount))}</span></div>
    <div class="row"><span>Paid (${escapeHtml(order.paymentMethodText)})</span><span>${escapeHtml(money(order.totalAmount))}</span></div>
    <div class="row"><span>Status</span><span>${escapeHtml(order.paymentStatusText || order.statusText)}</span></div>
  </div>

  <hr class="dash" />

  <div class="center">
    <p class="thanks">*** THANK YOU ***</p>
    <p class="tiny">Visit again · Goods once sold are not returnable<br/>unless defective as per store policy</p>
    <p class="barcode">*${escapeHtml(order.orderNumber)}*</p>
  </div>
</body>
</html>`;
}

export function printThermalReceipt(
  order: OrderResponseDto,
  options?: { paperWidthMm?: ThermalPaperWidth },
) {
  const html = buildThermalReceiptHtml(order, options);
  const paperWidthMm = options?.paperWidthMm ?? 80;
  const pixelWidth = paperWidthMm === 58 ? 280 : 360;

  const printWindow = window.open("", "_blank", `width=${pixelWidth},height=900`);
  if (!printWindow) {
    // Popup blocked: write into a hidden iframe and print that.
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      document.body.removeChild(iframe);
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();
    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 500);
    };
    return;
  }

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 350);
}
